import React, { useEffect, useState } from 'react';
import PaymentRequisitionsExportButton from '../components/PaymentRequisitionsExportButton';
import {
    Box, Button, CircularProgress, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography, TextField,
    MenuItem, Dialog, IconButton, Paper, Stack, FormControl,
    InputLabel, Select, FormControlLabel, Switch, styled, keyframes,
    SelectChangeEvent,
    DialogActions,
    DialogContent,
    DialogTitle,
    FilledTextFieldProps,
    OutlinedTextFieldProps,
    StandardTextFieldProps,
    TextFieldVariants,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Attachment,
    PaymentRequisition,
    useGetPaymentRequisitionsQuery,
    useLazyGetProcurementProcessesQuery,
    usePatchPaymentRequisitionsByIdMutation
} from '../../../redux/vmsApi';
import config from "../../../config";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NumbersIcon from '@mui/icons-material/Numbers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import DropzoneArea from '../../common/DropzoneArea';
import { useAppSelector } from '../../../redux/hooks';
import { getCurrentDate, getDefaultDueDate } from "../../../utils/dateUtil";

import { toast } from 'react-toastify';
import LoadingCover from '../../common/LoadingCover';
import { useRouter } from "../../../hooks/useRouter";
import { JSX } from 'react/jsx-runtime';
import ConfirmPaymentDialog from './ConfirmPaymentDialog';

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
`;

const DangerTableCell = styled(TableCell)(({ theme }) => ({
    animation: `${pulse} 1.5s infinite`,
    background: 'linear-gradient(45deg, #ff4d4d, #cc0000)',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    textAlign: 'right',
    padding: theme.spacing(1.5),
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
}));

const PaymentScheduleList: React.FC = () => {
    const router = useRouter();
    const [isClicked, setIsClicked] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const user = useAppSelector(state => state.auth.user);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [filters, setFilters] = useState({
        priority: '',
        amount: '',
        paymentMethod: '',
        paymentType: '',
        status: 'Scheduled',
        supplierName: '',
        development: '',
        project: '',
        department: '',
        reqNumber: '',
        purchaseOrderNumber: '',
        fromDate: '2025-01-06',
        toDate: getCurrentDate(),
        isOutstandingPayment: false,
        currency: '',
        reportByEmail: false,
        reportName: ''
    });
    const [triggerProcurementProcess] = useLazyGetProcurementProcessesQuery();
    const [openDialog, setOpenDialog] = useState<string | null>(null);
    const [currentOpenRow, setCurrentOpenRow] = useState<string | null>(null);
    const [reportByEmailEnabled, setReportByEmailEnabled] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });
    const [referenceNumbersByProcessId, setReferenceNumbersByProcessId] = useState<Record<string, string>>({});

    const { data: paymentRequisitions, isLoading, isError } =
        useGetPaymentRequisitionsQuery(apiParams);

    useEffect(() => {
        const missingProcessIds = Array.from(new Set(
            (paymentRequisitions || [])
                .filter((req: any) => !req?.purchaseOrder?.purchaseOrderNumber && req?.procurementProcessId)
                .map((req: any) => req.procurementProcessId)
                .filter((processId: string | undefined): processId is string => Boolean(processId))
                .filter((processId: string) => !referenceNumbersByProcessId[processId])
        ));

        if (missingProcessIds.length === 0) return;

        let cancelled = false;

        Promise.all(
            missingProcessIds.map(async (procurementProcessId) => {
                const { data } = await triggerProcurementProcess({ procurementProcessId });
                const process = data?.[0];
                return {
                    procurementProcessId,
                    referenceNumber: process?.requisition?.reqNumber || procurementProcessId,
                };
            })
        ).then((results) => {
            if (cancelled) return;

            setReferenceNumbersByProcessId((prev) => {
                const next = { ...prev };
                results.forEach(({ procurementProcessId, referenceNumber }) => {
                    next[procurementProcessId] = referenceNumber;
                });
                return next;
            });
        });

        return () => {
            cancelled = true;
        };
    }, [paymentRequisitions, triggerProcurementProcess, referenceNumbersByProcessId]);

    const handleFilterChange = (field: string, value: string | boolean) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleApplyFilters = () => {
        setApiParams({
            ...filters,
            skip: 0,
            limit: pageSize,
        });
        setPage(0);
        setReportByEmailEnabled(false);
        handleFilterChange('reportName', '');
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setApiParams((prevParams) => ({
            ...prevParams,
            skip: newPage * pageSize,
        }));
    };

    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        const newSize = Number(event.target.value);
        setPageSize(newSize);
        setPage(0);
        setApiParams((prevParams: any) => ({
            ...prevParams,
            limit: newSize,
            skip: 0,
        }));
    };

    //form patching
    const [updatePaymentReq] = usePatchPaymentRequisitionsByIdMutation();
    const handleFormChange = (field: keyof PaymentRequisition, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState<Partial<PaymentRequisition>>({
        paymentMethod: '',
        referenceNumber: '',
        amount: 0,
        rate: 1,
        dueDate: getDefaultDueDate(),
        paymentDate: '',
        status: 'Paid',
        priority: 0,
        documents: []
    });
    const [currentPaymentReq, setCurrentPaymentReq] = useState<PaymentRequisition | null>(null);
    const selectedPurchaseOrder = currentPaymentReq?.purchaseOrder;
    const paymentCurrency = selectedPurchaseOrder?.quotation?.currency || currentPaymentReq?.currency || 'USD';
    const paymentReferenceValue = selectedPurchaseOrder?.quotation?.grandTotal ?? currentPaymentReq?.amount;
    const handleEditClick = (paymentReq: PaymentRequisition) => {
        setCurrentPaymentReq(paymentReq);
        setFormData({
            ...paymentReq,
            paymentDate: '',
            status: 'Paid',
        });
        setAttachments([]);
        setIsDialogOpen(true);
    };

    const handleSubmit = async () => {
        setIsClicked(true);
        setShowOverlay(true);
        if (formData?.dueDate && formData?.paymentDate && formData?.referenceNumber && formData?.paymentMethod && formData.amount && user)
            try {
                const commonData = {
                    status: 'Paid' as const,
                    lastActionedDate: new Date().toISOString(),
                    paymentDate: formData?.paymentDate,
                    paymentConfirmedBy: user,
                    referenceNumber: formData.referenceNumber,
                    priority: 0,
                    documents: attachments,
                };

                if (currentPaymentReq && currentPaymentReq.id) {

                    await updatePaymentReq({
                        id: currentPaymentReq.id,
                        paymentRequisitionPartial: {
                            ...commonData,
                        }
                    }).unwrap();
                }

                handleEditDialogClose();
                // toast.success("Payment confirmation Successful", {
                //     position: toast.POSITION.TOP_RIGHT,
                // });
            } catch (error) {
                // toast.error("Error confirming Payment", {
                //     position: toast.POSITION.TOP_RIGHT,
                // });
            }

        setShowOverlay(false);
    };
    const handleEditDialogClose = () => {
        setIsDialogOpen(false);
        setCurrentPaymentReq(null);
        setFormData({
            paymentMethod: '',
            referenceNumber: '',
            amount: 0,
            rate: 1,
            dueDate: '',
            paymentDate: '',
            status: 'Paid'
        });
        setIsClicked(false);
        setAttachments([]);
    };

    const handleDialogOpen = async (procurementProcessId: string, documentType: string) => {
        setOpenDialog(null);

        const { data: updatedProcurementProcesses } = await triggerProcurementProcess({ procurementProcessId });

        if (updatedProcurementProcesses?.[0]) {
            const { data: updatedProcurementProcesses } = await triggerProcurementProcess({ procurementProcessId });
            if (updatedProcurementProcesses?.[0]) {
                const documentUrl = `${config.CDN_URL}/procurement/${documentType === 'purchaseOrder'
                    ? updatedProcurementProcesses[0].purchaseOrderDocument?.name
                    : updatedProcurementProcesses[0].requisitionDocument?.name
                    }`;

                if (documentUrl) {
                    setOpenDialog(documentUrl);
                } else {
                    console.error('Document URL not found');
                    setOpenDialog(null);
                }
            }



        } else {
            console.error('Procurement process not found');
            setOpenDialog(null);
        }
    };
    const closeAction = () => setCurrentOpenRow(null);
    const handleDialogClose = () => setOpenDialog(null);

    const handleToggleDropdown = (rowId: string) => {
        setCurrentOpenRow(prev => (prev === rowId ? null : rowId));
    };

    const handleViewProcessDetails = (procurementProcessId: string) => {
        window.open(`/procurement/procurementProcess/${procurementProcessId}`, '_blank');
        setCurrentOpenRow(null);
    };

    if (isLoading) return <CircularProgress />;
    if (isError) return <div>Error fetching payment requisitions.</div>;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ padding: 3, borderRadius: 2, mb: 3, bgcolor: '#f9f9f9', boxShadow: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
                    Payment Requisitions
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    <TextField
                        label="Supplier Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.supplierName}
                        onChange={e => handleFilterChange('supplierName', e.target.value)}
                    />

                    <TextField
                        label="Requisition Number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
                        value={filters.reqNumber}
                        onChange={(e) => handleFilterChange('reqNumber', e.target.value)}
                    />
                    <TextField
                        label="Purchase Order Number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
                        value={filters.purchaseOrderNumber}
                        onChange={(e) => handleFilterChange('purchaseOrderNumber', e.target.value)}
                    />

                    <TextField
                        label="Development"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.development}
                        onChange={e => handleFilterChange('development', e.target.value)}
                    />

                    <TextField
                        label="Project"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.project}
                        onChange={e => handleFilterChange('project', e.target.value)}
                    />

                    <TextField
                        label="Department"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.department}
                        onChange={e => handleFilterChange('department', e.target.value)}
                    />

                    <TextField
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.fromDate}
                        onChange={e => handleFilterChange('fromDate', e.target.value)}
                    />

                    <TextField
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.toDate}
                        onChange={e => handleFilterChange('toDate', e.target.value)}
                    />

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="currency-select-label">Currency</InputLabel>
                        <Select
                            labelId="currency-select-label"
                            value={filters.currency || ''}
                            label="Currency"
                            onChange={(e) => handleFilterChange('currency', e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ZAR">ZAR</MenuItem>
                            <MenuItem value="ZIG">ZIG</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="payment-method-select-label">Payment Method</InputLabel>
                        <Select
                            labelId="payment-method-select-label"
                            value={filters.paymentMethod || ''}
                            label="Payment Method"
                            onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="Nostro">Nostro</MenuItem>

                        </Select>
                    </FormControl>


                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="payment-type-select-label">Payment Type</InputLabel>
                        <Select
                            labelId="payment-type-select-label"
                            value={filters.paymentType || ''}
                            label="Payment Type"
                            onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="DEPOSIT">DEPOSIT</MenuItem>
                            <MenuItem value="INTERIM">INTERIM</MenuItem>
                            <MenuItem value="FINAL PAYMENT">FINAL PAYMENT</MenuItem>
                            <MenuItem value="ONCE OFF">ONCE OFF</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Priority"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.priority}
                        onChange={e => handleFilterChange('priority', e.target.value)}
                    />
                    <TextField
                        label="Request Amount"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.amount}
                        onChange={e => handleFilterChange('amount', e.target.value)}
                    />


                    <FormControlLabel
                        control={
                            <Switch
                                checked={filters.isOutstandingPayment}
                                onChange={e => handleFilterChange('isOutstandingPayment', e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Show Outstanding Only"
                        sx={{ ml: 1 }}
                    />

                </Box>

                <Box sx={{ padding: 3, borderRadius: 2, mb: 3 }}>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={reportByEmailEnabled}
                                onChange={e => {
                                    const isEnabled = e.target.checked;
                                    setReportByEmailEnabled(isEnabled);
                                    handleFilterChange('reportByEmail', isEnabled);
                                    if (!isEnabled) handleFilterChange('reportName', '');
                                }}
                                color="primary"
                            />
                        }
                        label="Email Report"
                    />

                    {reportByEmailEnabled && (
                        <TextField
                            label="Report Name"
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                            value={filters.reportName}
                            onChange={e => handleFilterChange('reportName', e.target.value)}
                        />
                    )}
                </Box>

                <Stack direction="row" spacing={2} mt={2}>
                    <Button
                        variant="outlined"
                        size="large"
                        fullWidth
                        onClick={handleApplyFilters}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: '#1976d2',
                            borderColor: '#1976d2',
                            '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1565c0' },
                        }}
                    >
                        Apply Filters
                    </Button>
                    <PaymentRequisitionsExportButton
                        apiParams={apiParams}
                        reportLabel="Scheduled Payment Requisitions"
                        fileNamePrefix="scheduled-payment-requisitions"
                    />
                </Stack>
            </Box>



            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#1976d2' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reference</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Development</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Method/Batch</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Priority</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paymentRequisitions && paymentRequisitions.length > 0 ? (
                            paymentRequisitions.map((req: any) => {
                                const isOverdue = new Date(req.dueDate) < new Date();

                                return (
                                    <TableRow key={req.id} hover>
                                        <TableCell>{req.purchaseOrder?.purchaseOrderNumber || referenceNumbersByProcessId[req.procurementProcessId] || req.procurementProcessId || req.purchaseOrderNumber || 'N/A'}</TableCell>
                                        <TableCell>{req.purchaseOrder?.title || req.title || 'N/A'}</TableCell>
                                        <TableCell>{req?.development || 'N/A'},{req?.project || 'N/A'},{req?.department || 'N/A'}</TableCell>

                                        <TableCell align="right">
                                            {isOverdue ? (
                                                <DangerTableCell>
                                                    <WarningIcon />
                                                    {new Date(req.dueDate).toLocaleDateString('en-GB', {
                                                        weekday: 'short',
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </DangerTableCell>
                                            ) : (
                                                new Date(req.dueDate).toLocaleDateString()
                                            )}
                                        </TableCell>

                                        <TableCell>{req.purchaseOrder?.quotation?.supplier?.name || 'N/A'}</TableCell>

                                        <TableCell>{req.paymentMethod},{req.paymentType}
                                            <Box
                                                sx={{
                                                    bgcolor:
                                                        req.status === 'Paid' ? '#4caf50' :
                                                            req.status === 'Rejected' ? '#f44336' :
                                                                req.status === 'Approved' ? '#2196f3' :
                                                                    req.status === 'Prioritized' ? '#ffb300' :
                                                                        req.status === 'Scheduled' ? '#9c27b0' :
                                                                            '#ff9800',
                                                    color: 'white',
                                                    borderRadius: '4px',
                                                    px: 1,
                                                    py: 0.5,
                                                    display: 'inline-block',
                                                    fontWeight: 500,
                                                    textAlign: 'center'
                                                }}

                                            >
                                                {req.status}   {req.batchNumber}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            {req.currency} {req.amount?.toFixed(2)}
                                        </TableCell>

                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '50%',
                                                        backgroundColor: [
                                                            '#f44336',
                                                            '#e53935',
                                                            '#d32f2f',
                                                            '#c62828',
                                                            '#ff5722',
                                                            '#ff9800',
                                                            '#ffc107',
                                                            '#ffeb3b',
                                                            '#8bc34a',
                                                            '#4caf50',
                                                        ][req.priority - 1],
                                                        color: req.priority <= 3 ? '#fff' : '#000',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.875rem',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        border: '2px solid #fff',
                                                    }}
                                                >
                                                    {req.priority}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        backgroundColor: '#e0f7fa',
                                                        color: '#006064',
                                                        paddingX: 1,
                                                        paddingY: 0.3,
                                                        borderRadius: '8px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        textAlign: 'center',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: '100%',
                                                    }}
                                                >
                                                    {req.department}
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        <TableCell align="right">
                                            <Box sx={{ position: "relative", display: "inline-block" }}>
                                                <Button
                                                    sx={{
                                                        bgcolor: "primary.main",
                                                        color: "white",
                                                        "&:hover": { bgcolor: "primary.dark" },
                                                    }}
                                                    onClick={() => handleToggleDropdown(req.id || "")}
                                                >
                                                    Actions
                                                </Button>

                                                {currentOpenRow === req.id && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            top: "100%",
                                                            right: 0,
                                                            zIndex: 1,
                                                            mt: 1,
                                                            minWidth: "150px",
                                                            bgcolor: "white",
                                                            boxShadow: 3,
                                                            borderRadius: 1,
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        <IconButton
                                                            onClick={closeAction}
                                                            sx={{
                                                                position: "absolute",
                                                                top: 4,
                                                                right: 4,
                                                                bgcolor: "white",
                                                                border: "1px solid red",
                                                                color: "red",
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                        <Button
                                                            sx={{
                                                                display: "block",
                                                                mt: 10,
                                                                width: "100%",
                                                                textAlign: "left",
                                                                px: 2,
                                                                py: 1,
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                            onClick={() => handleViewProcessDetails(req.purchaseOrder?.procurementProcessId || req.procurementProcessId || "")}
                                                        >
                                                            Procurement Process Details
                                                        </Button>

                                                        <Button
                                                            sx={{
                                                                display: "block",
                                                                width: "100%",
                                                                textAlign: "left",
                                                                px: 2,
                                                                py: 1,
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                            onClick={() => handleEditClick(req)}
                                                        >
                                                            Confirm Payment
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    No payment requisitions found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                <Button
                    onClick={() => handlePageChange(Math.max(page - 1, 0))}
                    disabled={page === 0}
                    variant="contained"
                    sx={{ mr: 2 }}
                >
                    Previous
                </Button>

                <Typography variant="body1">Page {page + 1}</Typography>

                <Button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={paymentRequisitions?.length === undefined || paymentRequisitions.length < pageSize}
                    variant="contained"
                    sx={{ ml: 2 }}
                >
                    Next
                </Button>
            </Box>

            <Box mt={2} display="flex" justifyContent="center" alignItems="center">
                <Typography variant="body2" sx={{ mr: 2 }}>Rows per page:</Typography>
                <FormControl variant="outlined" size="small">
                    <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        sx={{ bgcolor: '#f0f4f7' }}
                    >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
                <Box p={2}>
                    {openDialog && (
                        <iframe
                            src={openDialog}
                            title="Purchase Order Document"
                            width="100%"
                            height="500px"
                            style={{ border: 'none' }}
                        />
                    )}
                    <Box textAlign="right" mt={2}>
                        <Button onClick={handleDialogClose} variant="contained" color="primary">
                            Close
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <ConfirmPaymentDialog
                open={isDialogOpen}
                currentPaymentReq={currentPaymentReq}
                formData={formData}
                referenceNumbersByProcessId={referenceNumbersByProcessId}
                attachments={attachments}
                setAttachments={setAttachments}
                showOverlay={showOverlay}
                isSubmitting={isClicked}
                onClose={handleEditDialogClose}
                onSubmit={handleSubmit}
                onFormChange={handleFormChange}
            />
        </LocalizationProvider>
    );
};

export default PaymentScheduleList;